import type { OrthodontistFieldDef } from './orthodontistCreateFields';

// Lean sibling to practice/details/DetailField, without its per-field save
// state machine or PracticeFieldDef coupling - this form only ever submits once.
const SPAN_CLASS: Record<number, string> = {
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    9: 'col-span-9'
};

interface OrthodontistFormFieldProps {
    field: OrthodontistFieldDef;
    value: string;
    options: string[];
    error?: string;
    disabled: boolean;
    onChange: (value: string) => void;
}

export function OrthodontistFormField({
    field,
    value,
    options,
    error,
    disabled,
    onChange
}: OrthodontistFormFieldProps) {
    const inputId = `orthodontist-field-${field.key}`;
    const inputClass =
        'flex-1 min-w-0 bg-transparent outline-none text-[13px] text-gray-800 placeholder-gray-400 disabled:cursor-not-allowed';

    return (
        <div className={`flex flex-col gap-1.5 min-w-0 ${SPAN_CLASS[field.span] ?? ''}`}>
            <label
                htmlFor={inputId}
                className="text-[12px] font-semibold text-gray-500 flex items-center gap-1.5"
            >
                {field.label}
                {field.required && (
                    <span className="text-red-500 -ml-1" aria-hidden="true">
                        *
                    </span>
                )}
            </label>
            <div
                className={`relative border rounded-lg flex items-center gap-2 px-3 h-9 ${
                    error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                }`}
            >
                {field.type === 'select' ? (
                    <select
                        id={inputId}
                        value={value}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                        className={`${inputClass} appearance-none`}
                    >
                        <option value="">Not set</option>
                        {options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        id={inputId}
                        type={field.type}
                        value={value}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                        className={`${inputClass} ${field.mono ? 'font-mono text-[13px]' : ''}`}
                    />
                )}
            </div>
            {error && <span className="text-[12px] text-red-600">{error}</span>}
        </div>
    );
}
